import { AppData, Colors, Lightning, Router } from '@lightningjs/sdk';
import Button from 'components/common/buttons/Button';
import TextLabel from 'components/common/TextLabel';
import { RoutePath } from 'config/routes';
import { reportGetStarted } from 'services/analytics/reportingServicePage';
import { getFontFaceFromStyle } from 'support/textUtils';
import { PageId } from 'types/pageId';

const HEADER_HEIGHT = 131;
const CONTENT_WIDTH = 1685;
const BANNER_RADIUS = 12;

const TEXT_CONTAINER_WIDTH = 1422;
const TITLE_HEIGHT = 33;
const TITLE_FONT_SIZE = 28;
const DESCRIPTION_HEIGHT = 62;
const DESCRIPTIION_FONT_SIZE = 22;
const DESCRIPTION_WRAP_WIDTH = 1200;

const MARGIN_LARGE = 24;
const MARGIN_MEDIUM = 16;
const MARGIN_SMALL = 4;

const TEXT_BUTTON_SPACING = 36;

const BUTTON_HEIGHT = 60;
const BUTTON_MIN_WIDTH = 179;
const BUTTON_FONT_SIZE = 23;

export interface LoggedOutBannerTemplateSpec
  extends Lightning.Component.TemplateSpec {
  TextContainer: {
    Title: typeof TextLabel;
    Description: typeof TextLabel;
  };
  Button: typeof Button;
}

export default class LoggedOutBanner
  extends Lightning.Component<LoggedOutBannerTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LoggedOutBannerTemplateSpec>
{
  private _Button = this.getByRef('Button')!;
  private _Title = this.getByRef('TextContainer')!.getByRef('Title')!;
  private _Description =
    this.getByRef('TextContainer')!.getByRef('Description')!;

  get announce() {
    return [this._Title.text?.text, this._Description.text?.text];
  }

  get button() {
    return this._Button;
  }

  static override _template(): Lightning.Component.Template<LoggedOutBannerTemplateSpec> {
    return {
      flex: { direction: 'row' },
      rect: true,
      w: CONTENT_WIDTH,
      h: HEADER_HEIGHT,
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BANNER_RADIUS,
      },

      // Linear Gradient
      colorLeft: Colors('modalBackground').get(),
      colorRight: Colors('myStuffBannerGradient').alpha(0.9).get(),

      TextContainer: {
        flex: { direction: 'column' },
        flexItem: {
          marginLeft: MARGIN_LARGE,
          marginTop: MARGIN_MEDIUM,
          marginRight: TEXT_BUTTON_SPACING,
        },
        w: TEXT_CONTAINER_WIDTH,
        Title: {
          type: TextLabel,
          flexItem: { marginBottom: MARGIN_SMALL },
          h: TITLE_HEIGHT,
          text: {
            fontSize: TITLE_FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
          },
        },
        Description: {
          type: TextLabel,
          h: DESCRIPTION_HEIGHT,
          text: {
            wordWrapWidth: DESCRIPTION_WRAP_WIDTH,
            fontSize: DESCRIPTIION_FONT_SIZE,
            fontFace: getFontFaceFromStyle('light'),
            lineHeight: DESCRIPTION_HEIGHT / 2,
          },
        },
      },
      Button: {
        flexItem: { alignSelf: 'center', marginRight: MARGIN_LARGE, shrink: 0 },
        type: Button,
        height: BUTTON_HEIGHT,
        minWidth: BUTTON_MIN_WIDTH,
        fontSize: BUTTON_FONT_SIZE,
        fontFace: getFontFaceFromStyle('regular'),
        action: '$getStarted',
        signals: {
          $getStarted: true,
        },
        passSignals: { $onHover: true },
      },
    };
  }

  override _enable() {
    const loggedOutBanner = AppData?.config?.login.logged_out.banner;
    this._Title.patch({ text: { text: loggedOutBanner?.title } });
    this._Description.patch({ text: { text: loggedOutBanner?.description } });
    this._Button.patch({ label: loggedOutBanner?.cta_button_text });
  }

  override _getFocused() {
    return this._Button;
  }

  private $getStarted() {
    reportGetStarted(PageId.MY_STUFF);
    Router.navigate(RoutePath.LOGIN);
  }
}
